import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Link, useNavigate, useLocation } from "react-router-dom";

// eslint-disable-next-line import/no-unresolved
import buildConfig from "@regional/config/build.config.json";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import DescriptionIcon from "@mui/icons-material/Description";
import MoneyIcon from "@mui/icons-material/Money";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import HeaderLogo from "../HeaderLogo";
import LocalLoader from "../../../../components/Loaders/Local";
import AccountCard from "./AccountCard";
import ErrorBoundary from "../../../../ErrorBoundary";
import classes from "./navigation.module.css";

import { useUserTenantsQuery } from "../../../../api/tenants.api.slice";
import { CustomThemeContext } from "../../../../theme/CustomThemeProvider";
import { isAuthenticated } from "../../../../services/auth.service";
import { useSwitchAccountMutation } from "../../../../api/auth.api.slice";
import { getLogin } from "../../../auth/slices/auth.slice";
import { openDialog } from "../../slices/dialog.slice";
import { showNotification } from "../../slices/notification.slice";
import { primaryLight } from "../../../../theme/color.constants";
import { isSSOEnabled } from "../../../../utils/helpers";

const switchAccPermission = "switch_account";

const getListStyle = (theme) => ({
  "&& .Mui-selected, && .Mui-selected:hover": {
    backgroundColor: theme.palette.primary.light,
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.primary.dark,
    }),
    "&, & .MuiSvgIcon-root": {
      color: "white",
    },
  },
});

function NavItems(props) {
  const iconMap = {
    CardMembershipIcon: <CardMembershipIcon />,
    DescriptionIcon: <DescriptionIcon />,
    MoneyIcon: <MoneyIcon />,
  };
  const { listItems, drawerHandler, pathName } = props;
  const sortedListItems = [...listItems].sort(
    (arg1, arg2) => arg1.order - arg2.order
  );
  return (
    <List sx={getListStyle}>
      {sortedListItems.map((item) => (
        <ListItem disablePadding key={item.systemName}>
          <ListItemButton
            component={Link}
            to={item.link}
            onClick={drawerHandler}
            onKeyDown={drawerHandler}
            // selected={pathName !== "" && item.link.includes(pathName)}
            selected={pathName !== "" && pathName.includes(item.link.slice(1))}
          >
            <ListItemIcon sx={{ color: primaryLight }}>
              {iconMap[item.icon]}
            </ListItemIcon>
            <ListItemText primary={item.displayName} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}
NavItems.propTypes = {
  listItems: PropTypes.oneOfType([PropTypes.array]).isRequired,
  drawerHandler: PropTypes.func.isRequired,
  pathName: PropTypes.string.isRequired,
};

function Navigation(props) {
  const { currentTenant, closeDrawer } = props;
  const [skip, setSkip] = useState(true);
  const { navConfig } = useSelector((state) => state.navConfig);
  const { permissionList: permissions, userData } = useSelector(
    (state) => state.auth
  );
  const { data: tenantList } = useUserTenantsQuery(userData?.href, { skip });
  const [switchAccount, { isLoading }] = useSwitchAccountMutation();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [navState, setNavState] = React.useState({});

  const limitIndAccCreation =
    navConfig?.createAccount?.limit?.individualAccounts &&
    tenantList?.filter((tenant) => tenant.type === "Individual").length >=
      navConfig?.createAccount?.limit?.individualAccounts;

  const switchTenantHandler = useCallback(async (id) => {
    try {
      await switchAccount(id).unwrap();
      dispatch(getLogin());
      dispatch(
        showNotification({
          type: "success",
          msg: "Account switched Successfully.",
          show: true,
        })
      );

      navigate("/licenses", { replace: true, state: "switchTenant" });
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          msg:
            error?.data?.message ||
            "Could not process request, please try later.",
          show: true,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (item) => {
    setNavState({ ...navState, [item]: !navState[item] });
  };
  const accountCard = useMemo(
    () =>
      !userData?.addNewUser && (
        <ErrorBoundary>
          <AccountCard
            tenantList={tenantList}
            permissions={permissions}
            currentTenant={currentTenant}
            switchTenantHandler={switchTenantHandler}
          />
        </ErrorBoundary>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantList, currentTenant]
  );

  useEffect(() => {
    if (isAuthenticated() && !userData.addNewUser) {
      // dispatch(getNavigation());
      if (permissions && permissions.indexOf(switchAccPermission) === -1) {
        setSkip(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pathname.includes("accounts")) {
      setNavState({ ...navState, account: true });
    }
    if (
      pathname.includes("tenants") ||
      pathname.replace(/[^\w\s]/gi, "") === "users" ||
      pathname.includes("iamusers")
    ) {
      setNavState({ ...navState, "control-panel": true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isSSOEnabled && userData?.addNewUser) return null;

  return (
    <>
      <nav aria-label="Main navigation">
        <div id="mainSideNav" tabIndex="-1">
          {currentTenant && permissions?.length > 0 && (
            <List>
              <ListItem
                alignItems="center"
                tabIndex="-1"
                aria-label="account Card"
              >
                {accountCard}
              </ListItem>
            </List>
          )}
          {navConfig && navConfig.items && (
            <NavItems
              listItems={navConfig.items.filter((item) => item?.enabled)}
              drawerHandler={closeDrawer}
              pathName={pathname.replace(/[^\w\s]/gi, "")}
            />
          )}
          <Divider aria-hidden="true" />
          <List>
            <ListItem disablePadding>
              <ListItemButton
                aria-label="Account expand"
                onClick={() => handleClick("account")}
              >
                <ListItemIcon sx={{ color: primaryLight }}>
                  <AccountBalanceWalletIcon />
                </ListItemIcon>
                <ListItemText primary="Account" />
                {navState.account ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse
              in={navState.account}
              timeout="auto"
              unmountOnExit
              component="li"
            >
              <List disablePadding sx={getListStyle}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to={`/accounts/${currentTenant?._id}/profile`}
                    sx={{ color: "white", pl: 3 }}
                    onClick={closeDrawer}
                    onKeyDown={closeDrawer}
                    selected={pathname.includes("accounts")}
                  >
                    <ListItemIcon sx={{ color: "white" }}>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Settings" />
                  </ListItemButton>
                </ListItem>
                {navConfig &&
                  navConfig?.createAccount?.Individual &&
                  !limitIndAccCreation && (
                    <ListItem disablePadding>
                      <ListItemButton
                        name="individual"
                        sx={{ color: "white", pl: 3 }}
                        onClick={(e) => {
                          dispatch(
                            openDialog({ type: "Individual", show: true })
                          );
                          closeDrawer(e);
                        }}
                        onKeyDown={closeDrawer}
                      >
                        <ListItemIcon sx={{ color: "white" }}>
                          <PersonAddIcon />
                        </ListItemIcon>
                        <ListItemText primary="Add Individual" />
                      </ListItemButton>
                    </ListItem>
                  )}
                {navConfig && navConfig?.createAccount?.Business && (
                  <ListItem disablePadding>
                    <ListItemButton
                      name="business"
                      sx={{ color: "white", pl: 3 }}
                      onClick={(e) => {
                        dispatch(openDialog({ type: "Business", show: true }));
                        closeDrawer(e);
                      }}
                      onKeyDown={closeDrawer}
                    >
                      <ListItemIcon sx={{ color: "white" }}>
                        <BusinessIcon />
                      </ListItemIcon>
                      <ListItemText primary="Add Business" />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Collapse>
            {permissions &&
              permissions.indexOf("view_control_panel") !== -1 && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      aria-label="control Panel expand"
                      onClick={() => handleClick("control-panel")}
                    >
                      <ListItemIcon sx={{ color: primaryLight }}>
                        <TuneIcon />
                      </ListItemIcon>
                      <ListItemText primary="Control Panel" />
                      {navState["control-panel"] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse
                    in={navState["control-panel"]}
                    timeout="auto"
                    unmountOnExit
                    component="li"
                  >
                    <List disablePadding sx={getListStyle}>
                      <ListItem disablePadding>
                        <ListItemButton
                          component={Link}
                          to="/tenants"
                          sx={{ color: "white", pl: 3 }}
                          onClick={closeDrawer}
                          onKeyDown={closeDrawer}
                          selected={pathname.includes("tenants")}
                        >
                          <ListItemIcon sx={{ color: "white" }}>
                            <AllInboxIcon />
                          </ListItemIcon>
                          <ListItemText primary="Accounts" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton
                          component={Link}
                          to="/users"
                          sx={{ color: "white", pl: 3 }}
                          onClick={closeDrawer}
                          onKeyDown={closeDrawer}
                          selected={
                            pathname.replace(/[^\w\s]/gi, "") === "users"
                          }
                        >
                          <ListItemIcon sx={{ color: "white" }}>
                            <AccountTreeIcon />
                          </ListItemIcon>
                          <ListItemText primary="Relationships" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton
                          component={Link}
                          to="/iamusers"
                          sx={{ color: "white", pl: 3 }}
                          onClick={closeDrawer}
                          onKeyDown={closeDrawer}
                          selected={pathname.includes("iamusers")}
                        >
                          <ListItemIcon sx={{ color: "white" }}>
                            <GroupIcon />
                          </ListItemIcon>
                          <ListItemText primary="Users" />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              )}
          </List>
        </div>
      </nav>
      <LocalLoader progress={isLoading} />
    </>
  );
}

function NavigationWrapper(props) {
  const { currentTenant = null } = props;
  const { drawerVisible, setDrawer, tabBelow } = useContext(CustomThemeContext);
  const isNewUser = useSelector((state) => state.auth?.userData?.addNewUser);

  const closeDrawer = useCallback(
    (event) => {
      if (
        event.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }
      setDrawer(!tabBelow);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setDrawer, tabBelow]
  );

  const logoClassMap = { default: {}, moLogo: { padding: "0.1875rem 0" } };
  const logoStyles =
    logoClassMap[buildConfig?.logoClass] || logoClassMap.default;

  return (
    <Drawer
      variant={tabBelow ? "temporary" : "persistent"}
      open={!isNewUser && drawerVisible}
      onClose={closeDrawer}
      sx={{
        right: "auto !important",
        ".MuiPaper-root": {
          width: "18.4375rem",
          top: 0,
        },
      }}
      anchor="left"
    >
      <List disablePadding aria-label="go to home">
        <ListItemButton className={classes.navHeaderLogo} sx={logoStyles}>
          <HeaderLogo />
          <IconButton
            aria-label="Close navigation menu"
            onClick={closeDrawer}
            sx={{
              position: "absolute",
              top: "0",
              right: "0.1em",
              display: { xs: "block", md: "none" },
            }}
          >
            <HighlightOffIcon color="secondary" />
          </IconButton>
        </ListItemButton>
      </List>
      <Divider aria-hidden="true" />
      <ErrorBoundary key="leftNav" name="leftNav">
        <Navigation currentTenant={currentTenant} closeDrawer={closeDrawer} />
      </ErrorBoundary>
    </Drawer>
  );
}

export default NavigationWrapper;
NavigationWrapper.propTypes = {
  currentTenant: PropTypes.oneOfType([PropTypes.object]),
};
NavigationWrapper.defaultProps = {
  currentTenant: null,
};
